import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaCheckCircle } from "react-icons/fa";
import {
  addAdditionalColleagues,
  getClassesBySchoolId,
  getTeacherAccountLookup,
  getTeachersBySchoolId,
  requestPasswordReset,
  resendTeacherSetupLink,
  sendBatchTeacherInvites,
  shareClass
} from "../services/APIClient";
import "./TeacherInviteTeacherModal.css";

const GRADE_LEVELS = [
  { value: "grade_level1", label: "Junior Infants" },
  { value: "grade_level2", label: "Senior Infants" },
  { value: "grade_level3", label: "1st Class" },
  { value: "grade_level4", label: "2nd Class" },
  { value: "grade_level5", label: "3rd Class" },
  { value: "grade_level6", label: "4th Class" },
  { value: "grade_level7", label: "5th Class" },
  { value: "grade_level8", label: "6th Class" }
];

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ");

const normaliseComparableText = (value) => normaliseText(value).toLowerCase();

const firstPresentValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && String(value).trim() !== "") || "";

const getClassId = (classItem) => classItem?.id || classItem?._id;

const getGradeLabelFromClass = (classItem) => {
  const grade = GRADE_LEVELS.find((level) => classItem?.[level.value]);
  return grade?.label || classItem?.grade || classItem?.gradeLevel || "";
};

const getAcademicYearFromClass = (classItem) =>
  classItem?.academicYear ||
  classItem?.academic_year ||
  classItem?.academicyear ||
  classItem?.schoolYear ||
  classItem?.yearLabel ||
  "";

const normaliseClass = (classItem) => {
  const grade = getGradeLabelFromClass(classItem);
  const academicYear = getAcademicYearFromClass(classItem);
  const studentCount =
    classItem?.studentNumber ??
    classItem?.studentCount ??
    classItem?.studentsCount ??
    classItem?.students?.length;
  const studentCopy =
    Number.isFinite(Number(studentCount))
      ? `${studentCount} ${Number(studentCount) === 1 ? "child" : "children"}`
      : "";
  const detailLabel = [grade, academicYear, studentCopy].filter(Boolean).join(", ");

  return {
    ...classItem,
    id: getClassId(classItem),
    name: classItem?.name || classItem?.className || "Unnamed Class",
    grade,
    academicYear,
    detailLabel,
    searchText: [
      classItem?.name,
      classItem?.className,
      grade,
      academicYear
    ].filter(Boolean).join(" ").toLowerCase()
  };
};

const SETUP_LINK_STATUSES = new Set([
  "awaiting setup",
  "invitation sent",
  "invite sent",
  "invited",
  "not set up",
  "not setup",
  "pending",
  "pending teacher",
  "pending_teacher",
  "setup incomplete",
  "setup not completed",
  "setup_not_completed"
]);

const getTeacherEmail = (teacher) =>
  firstPresentValue(
    teacher?.email,
    teacher?.teacherEmail,
    teacher?.teacher_email,
    teacher?.teacheremail,
    teacher?.emailAddress,
    teacher?.email_address,
    teacher?.pendingEmail,
    teacher?.pending_email,
    teacher?.inviteEmail,
    teacher?.invite_email,
    teacher?.invitedEmail,
    teacher?.invited_email,
    teacher?.recipientEmail,
    teacher?.recipient_email,
    teacher?.userEmail,
    teacher?.user_email,
    teacher?.accountEmail,
    teacher?.account_email,
    teacher?.user?.email,
    teacher?.teacher?.email,
    teacher?.pendingTeacher?.email,
    teacher?.pending_teacher?.email
  );

const getPendingTeacherId = (teacher) =>
  firstPresentValue(
    teacher?.pendingteacherid,
    teacher?.pendingTeacherId,
    teacher?.pendingTeacherID,
    teacher?.pending_teacher_id,
    teacher?.pending_teacherid,
    teacher?.pendingTeacher?.id,
    teacher?.pending_teacher?.id
  );

const getTeacherStatus = (teacher) =>
  firstPresentValue(
    teacher?.status,
    teacher?.accountStatus,
    teacher?.account_status,
    teacher?.userStatus,
    teacher?.user_status,
    teacher?.inviteStatus,
    teacher?.invite_status
  );

const normaliseActions = (actions) => {
  if (Array.isArray(actions)) return actions;
  if (actions) return [actions];
  return [];
};

const teacherNeedsSetupLink = (teacher) => {
  const status = normaliseComparableText(teacher?.status).replace(/-/g, " ");
  const availableActions = normaliseActions(teacher?.availableActions || teacher?.available_actions);

  return (
    availableActions.includes("resend_setup_link") ||
    SETUP_LINK_STATUSES.has(status) ||
    Boolean(teacher?.pendingteacherid && !teacher?.canShareClasses)
  );
};

const normaliseTeacher = (teacher) => {
  const email = getTeacherEmail(teacher);
  const pendingteacherid = getPendingTeacherId(teacher);
  const status = getTeacherStatus(teacher);
  const activeTeacherId = firstPresentValue(
    teacher?.teacherid,
    teacher?.teacherId,
    teacher?.teacher_id,
    teacher?.teacher?.id
  );
  const id = firstPresentValue(
    pendingteacherid,
    teacher?.id,
    teacher?._id,
    activeTeacherId,
    teacher?.userId,
    teacher?.user_id,
    teacher?.userid,
    teacher?.accountId,
    teacher?.account_id,
    teacher?.accountid,
    email
  );
  const isPendingTeacher = Boolean(pendingteacherid) || SETUP_LINK_STATUSES.has(normaliseComparableText(status).replace(/-/g, " "));
  const shareTeacherId = firstPresentValue(activeTeacherId, !isPendingTeacher ? teacher?.id || teacher?._id : "");

  return {
    ...teacher,
    id,
    pendingteacherid,
    shareTeacherId,
    canShareClasses: Boolean(shareTeacherId),
    name:
      teacher?.name ||
      teacher?.fullName ||
      teacher?.displayName ||
      [teacher?.firstName, teacher?.lastName].filter(Boolean).join(" ") ||
      "Unnamed teacher",
    email,
    status,
    availableActions: normaliseActions(teacher?.availableActions || teacher?.available_actions),
    roleLabel:
      teacher?.roleLabel ||
      teacher?.position ||
      teacher?.title ||
      teacher?.role ||
      (Array.isArray(teacher?.roles) ? teacher.roles[0] : "") ||
      "Teacher"
  };
};

const getTeacherRows = (teachersResponse) => {
  if (Array.isArray(teachersResponse)) return teachersResponse;
  if (Array.isArray(teachersResponse?.teachers)) return teachersResponse.teachers;
  if (Array.isArray(teachersResponse?.rows)) return teachersResponse.rows;
  if (Array.isArray(teachersResponse?.data)) return teachersResponse.data;
  return [];
};

const getAccountLookupRows = (lookupResponse) => {
  if (Array.isArray(lookupResponse)) return lookupResponse;
  if (Array.isArray(lookupResponse?.data)) return lookupResponse.data;

  const collectionKeys = [
    "teachers",
    "rows",
    "accounts",
    "activeTeachers",
    "active_teachers",
    "pendingTeachers",
    "pending_teachers",
    "invitedTeachers",
    "invited_teachers",
    "pending",
    "invites",
    "users"
  ];

  return collectionKeys.flatMap((key) => {
    const directRows = lookupResponse?.[key];
    const dataRows = lookupResponse?.data?.[key];

    if (Array.isArray(directRows)) return directRows;
    if (Array.isArray(dataRows)) return dataRows;
    return [];
  });
};

const mergeTeacherRows = (...teacherGroups) => {
  const teachersByKey = new Map();

  teacherGroups.flat().forEach((teacher) => {
    const normalisedTeacher = normaliseTeacher(teacher);
    const key = normaliseComparableText(normalisedTeacher.email) || normalisedTeacher.id;

    if (!key) {
      return;
    }

    const existingTeacher = teachersByKey.get(key) || {};

    teachersByKey.set(key, {
      ...existingTeacher,
      ...normalisedTeacher,
      pendingteacherid: normalisedTeacher.pendingteacherid || existingTeacher.pendingteacherid,
      shareTeacherId: existingTeacher.shareTeacherId || normalisedTeacher.shareTeacherId,
      canShareClasses: Boolean(existingTeacher.canShareClasses || normalisedTeacher.canShareClasses),
      availableActions: [
        ...new Set([
          ...(existingTeacher.availableActions || []),
          ...(normalisedTeacher.availableActions || [])
        ])
      ]
    });
  });

  return Array.from(teachersByKey.values());
};

const normaliseClassNames = (value) => {
  if (Array.isArray(value)) {
    return value.map(normaliseText).filter(Boolean);
  }

  return String(value || "")
    .split(/[\n,]+/)
    .map(normaliseText)
    .filter(Boolean);
};

const normalisePrefill = (prefill) => {
  const classNames = [
    ...normaliseClassNames(prefill?.classNames || prefill?.class_names),
    ...normaliseClassNames(prefill?.className || prefill?.class_name)
  ];

  return {
    teacherName: normaliseText(prefill?.teacherName || prefill?.teacher_name),
    email: normaliseText(prefill?.email || prefill?.teacherEmail || prefill?.teacher_email),
    classNames
  };
};

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const findExactEmailMatch = (teachers, email) => {
  const normalisedEmail = normaliseComparableText(email);

  if (!normalisedEmail) {
    return null;
  }

  return teachers.find(
    (teacher) => normaliseComparableText(teacher.email) === normalisedEmail
  ) || null;
};

const getPrefilledClasses = (classes, classNames) => {
  const selectedById = new Map();

  classNames.forEach((className) => {
    const matches = classes.filter(
      (classItem) => normaliseComparableText(classItem.name) === normaliseComparableText(className)
    );

    if (matches.length === 1) {
      selectedById.set(matches[0].id, matches[0]);
    }
  });

  return Array.from(selectedById.values());
};

const buildPendingTeacherRows = (teacher, selectedClasses) => {
  const classIds = selectedClasses.map((classItem) => classItem.id).filter(Boolean);

  if (classIds.length < 1) {
    return [teacher];
  }

  return classIds.map((classId) => ({
    ...teacher,
    sharedClassId: classId
  }));
};

function TeacherInviteTeacherModal({
  show,
  handleClose,
  schoolid,
  teacherid,
  rollnumber = "",
  rollNumber = "",
  currentTeacherName = "",
  countryId = null,
  prefill = null,
  onReturnToTeacherDashboard = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("form");
  const [teacherName, setTeacherName] = useState("");
  const [teacherEmail, setTeacherEmail] = useState("");
  const [addClassesNow, setAddClassesNow] = useState(true);
  const [classSearchTerm, setClassSearchTerm] = useState("");
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [existingTeacher, setExistingTeacher] = useState(null);
  const [existingTeacherAction, setExistingTeacherAction] = useState("shareClasses");
  const [successAction, setSuccessAction] = useState("");
  const [inviteResults, setInviteResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [error, setError] = useState("");

  const prefillContext = useMemo(() => normalisePrefill(prefill), [prefill]);
  const trimmedTeacherName = normaliseText(teacherName);
  const trimmedTeacherEmail = normaliseText(teacherEmail);
  const emailIsValid = isValidEmail(trimmedTeacherEmail);
  const matchingTeacher = findExactEmailMatch(teachers, trimmedTeacherEmail);
  const existingTeacherCanShareClasses = Boolean(existingTeacher?.canShareClasses && existingTeacher?.shareTeacherId);
  const existingTeacherNeedsSetupLink = Boolean(existingTeacher && teacherNeedsSetupLink(existingTeacher));
  const canSendExistingSetupLink = Boolean(existingTeacherNeedsSetupLink && existingTeacher?.pendingteacherid);
  const hasSelectedClasses = selectedClasses.length > 0;
  const needsClassSelection = addClassesNow && !matchingTeacher;
  const canSendInvite = Boolean(
    trimmedTeacherName &&
    emailIsValid &&
    (!needsClassSelection || hasSelectedClasses) &&
    !isSubmitting
  );
  const canCompleteExistingTeacher = Boolean(
    existingTeacherAction === "passwordReset" ||
    (existingTeacherAction === "setupLink" && canSendExistingSetupLink) ||
    (existingTeacherAction === "shareClasses" && hasSelectedClasses && existingTeacherCanShareClasses)
  ) && !isSubmitting;
  const showInviteNeededNote = emailIsValid && !matchingTeacher && !isLoading;

  useEffect(() => {
    if (!show) {
      return;
    }

    setStage("form");
    setTeacherName(prefillContext.teacherName);
    setTeacherEmail(prefillContext.email);
    setAddClassesNow(true);
    setClassSearchTerm(prefillContext.classNames.join(", "));
    setClasses([]);
    setTeachers([]);
    setSelectedClasses([]);
    setExistingTeacher(null);
    setExistingTeacherAction("shareClasses");
    setSuccessAction("");
    setInviteResults([]);
    setSubmitAttempted(false);
    setError("");

    if (!schoolid) {
      setError("Classes and teachers could not be loaded. You can still fill the invite.");
      return;
    }

    setIsLoading(true);

    Promise.allSettled([
      getClassesBySchoolId(schoolid),
      getTeachersBySchoolId(schoolid),
      getTeacherAccountLookup(schoolid)
    ])
      .then(([classesResult, teachersResult, accountLookupResult]) => {
        const classesResponse = classesResult.status === "fulfilled" ? classesResult.value : [];
        const teachersResponse = teachersResult.status === "fulfilled" ? teachersResult.value : [];
        const accountLookupResponse = accountLookupResult.status === "fulfilled" ? accountLookupResult.value : [];

        const normalisedClasses = (Array.isArray(classesResponse) ? classesResponse : [])
          .map(normaliseClass)
          .filter((classItem) => classItem.id)
          .filter((classItem) => classItem.active !== false)
          .filter((classItem) => !classItem.snapshot && !classItem.trial_class)
          .sort((firstClass, secondClass) => {
            if (firstClass.academicYear !== secondClass.academicYear) {
              return secondClass.academicYear?.localeCompare(firstClass.academicYear || "") || 0;
            }

            return firstClass.name.localeCompare(secondClass.name);
          });
        const normalisedTeachers = mergeTeacherRows(
          getTeacherRows(teachersResponse),
          getAccountLookupRows(accountLookupResponse)
        )
          .filter((teacher) => teacher.id)
          .filter((teacher) => teacher.id !== teacherid)
          .sort((firstTeacher, secondTeacher) => firstTeacher.name.localeCompare(secondTeacher.name));

        setClasses(normalisedClasses);
        setTeachers(normalisedTeachers);
        setSelectedClasses(getPrefilledClasses(normalisedClasses, prefillContext.classNames));

        if (classesResult.status === "rejected" && teachersResult.status === "rejected" && accountLookupResult.status === "rejected") {
          setError("Classes and teachers could not be loaded. You can still fill the invite.");
        }
      })
      .catch((loadError) => {
        console.error("Error loading invite teacher data:", loadError);
        setError("Classes and teachers could not be loaded. You can still fill the invite.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    show,
    schoolid,
    teacherid,
    prefillContext.teacherName,
    prefillContext.email,
    prefillContext.classNames
  ]);

  const filteredClasses = useMemo(() => {
    const normalisedSearchTerm = normaliseComparableText(classSearchTerm);

    if (!normalisedSearchTerm) {
      return [];
    }

    return classes.filter((classItem) =>
      classItem.searchText.includes(normalisedSearchTerm)
    );
  }, [classes, classSearchTerm]);

  const toggleClass = (classItem) => {
    setSelectedClasses((currentClasses) => {
      if (currentClasses.some((selectedClass) => selectedClass.id === classItem.id)) {
        return currentClasses.filter((selectedClass) => selectedClass.id !== classItem.id);
      }

      return [...currentClasses, classItem];
    });
    setError("");
  };

  const clearClasses = () => {
    setAddClassesNow(false);
    setSelectedClasses([]);
    setError("");
  };

  const sendInvite = async () => {
    setSubmitAttempted(true);

    if (!canSendInvite) {
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      if (matchingTeacher) {
        setExistingTeacher(matchingTeacher);
        setExistingTeacherAction(
          hasSelectedClasses && matchingTeacher.canShareClasses
            ? "shareClasses"
            : teacherNeedsSetupLink(matchingTeacher)
              ? "setupLink"
              : "passwordReset"
        );
        setStage("teacherFound");
        return;
      }

      const nameParts = trimmedTeacherName.split(/\s+/);
      const newTeacher = {
        firstName: nameParts[0] || trimmedTeacherName,
        lastName: nameParts.slice(1).join(" "),
        name: trimmedTeacherName,
        email: trimmedTeacherEmail,
        roles: "Class teacher"
      };

      if (!schoolid) {
        throw new Error("Missing school context for teacher invite.");
      }

      const pendingTeacherRows = buildPendingTeacherRows(newTeacher, selectedClasses);
      const response = await addAdditionalColleagues(
        schoolid,
        rollnumber || rollNumber,
        pendingTeacherRows,
        currentTeacherName,
        countryId
      );
      const rows = response?.results || response?.rows || response?.users || response || [];
      setInviteResults(Array.isArray(rows) ? rows : pendingTeacherRows.map((teacher) => ({
        email: teacher.email,
        status: "invited"
      })));

      setSuccessAction("invite");
      setStage("success");
    } catch (inviteError) {
      console.error("Error inviting teacher:", inviteError);
      setError("The invite could not be sent. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const completeExistingTeacherAction = async () => {
    if (!canCompleteExistingTeacher) {
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      if (existingTeacherAction === "shareClasses") {
        await Promise.all(
          selectedClasses.map((classItem) => shareClass(classItem.id, existingTeacher.shareTeacherId))
        );
      } else if (existingTeacherAction === "setupLink") {
        await resendTeacherSetupLink(schoolid, existingTeacher.pendingteacherid);
      } else {
        await requestPasswordReset(existingTeacher.email);
      }

      setSuccessAction(existingTeacherAction);
      setStage("success");
    } catch (actionError) {
      console.error("Error completing existing teacher action:", actionError);
      setError("The teacher could not be updated. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForAnotherTeacher = () => {
    setStage("form");
    setTeacherName("");
    setTeacherEmail("");
    setAddClassesNow(true);
    setClassSearchTerm("");
    setSelectedClasses([]);
    setExistingTeacher(null);
    setExistingTeacherAction("shareClasses");
    setSuccessAction("");
    setInviteResults([]);
    setSubmitAttempted(false);
    setError("");
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: trimmedTeacherEmail || existingTeacher?.email || "",
        message: "Teacher invite workflow complete."
      });
      return;
    }

    handleClose();
  };

  const returnToTeacherDashboard = () => {
    if (embedded) {
      handleDone();
      return;
    }

    if (onReturnToTeacherDashboard) {
      onReturnToTeacherDashboard();
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const renderClassResults = () => {
    if (!addClassesNow) {
      return null;
    }

    if (isLoading) {
      return (
        <div className="invite-teacher-search-state">
          <Spinner animation="border" size="sm" />
          <span>Loading classes...</span>
        </div>
      );
    }

    if (!normaliseComparableText(classSearchTerm)) {
      return (
        <p className="invite-teacher-muted">
          Search for a class to choose access.
        </p>
      );
    }

    if (filteredClasses.length < 1) {
      return (
        <p className="invite-teacher-muted">
          No matching classes found.
        </p>
      );
    }

    return filteredClasses.map((classItem) => {
      const selected = selectedClasses.some((selectedClass) => selectedClass.id === classItem.id);

      return (
        <button
          key={classItem.id}
          type="button"
          className={`invite-teacher-result${selected ? " selected" : ""}`}
          onClick={() => toggleClass(classItem)}
          aria-label={`${selected ? "Remove" : "Select"} ${classItem.name}`}
          aria-pressed={selected}
        >
          <span className="invite-teacher-check" aria-hidden="true" />
          <span className="invite-teacher-result-copy">
            <strong>{classItem.name}</strong>
            {classItem.detailLabel && <span>{classItem.detailLabel}</span>}
          </span>
          {selected && <span className="invite-teacher-selected-label">Selected</span>}
        </button>
      );
    });
  };

  const renderSelectedClassSummary = () => {
    if (!hasSelectedClasses) {
      return "No classes selected.";
    }

    return selectedClasses.map((classItem) => classItem.name).join(", ");
  };

  const renderEmailStatus = () => {
    if (!showInviteNeededNote) {
      return null;
    }

    return (
      <Form.Text className="invite-teacher-email-note" aria-live="polite">
        No existing teacher found for this email. A setup invite will be sent.
      </Form.Text>
    );
  };

  const renderForm = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title>Invite Teacher</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="invite-teacher-intro">Send a setup invite and choose class access.</p>
        {error && <Alert variant="danger">{error}</Alert>}

        <div className="invite-teacher-form-grid">
          <Form.Group controlId="teacherInviteTeacherName">
            <Form.Label>Teacher name</Form.Label>
            <Form.Control
              type="text"
              value={teacherName}
              onChange={(event) => {
                setTeacherName(event.target.value);
                setError("");
              }}
              placeholder="Teacher name"
              aria-label="Teacher name"
              isInvalid={submitAttempted && !trimmedTeacherName}
            />
            <Form.Control.Feedback type="invalid">
              Enter the teacher's name.
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="teacherInviteTeacherEmail">
            <Form.Label>Teacher email</Form.Label>
            <Form.Control
              type="email"
              value={teacherEmail}
              onChange={(event) => {
                setTeacherEmail(event.target.value);
                setError("");
              }}
              placeholder="teacher@example.com"
              aria-label="Teacher email"
              isInvalid={submitAttempted && !emailIsValid}
            />
            <Form.Control.Feedback type="invalid">
              Enter a valid email address.
            </Form.Control.Feedback>
            {renderEmailStatus()}
          </Form.Group>
        </div>

        <Form.Group className="invite-teacher-section" controlId="teacherInviteClassChoice">
          <Form.Label>Class access</Form.Label>
          <div className="invite-teacher-options">
            <label className={`invite-teacher-option${addClassesNow ? " selected" : ""}`}>
              <Form.Check
                type="radio"
                name="teacherInviteClassChoice"
                checked={addClassesNow}
                onChange={() => {
                  setAddClassesNow(true);
                  setError("");
                }}
                aria-label="Yes, add classes now"
              />
              <span>
                <strong>Yes, add classes now</strong>
                <span>Choose one or more classes for this teacher.</span>
              </span>
            </label>
            <label className={`invite-teacher-option${!addClassesNow ? " selected" : ""}`}>
              <Form.Check
                type="radio"
                name="teacherInviteClassChoice"
                checked={!addClassesNow}
                onChange={clearClasses}
                aria-label="No class yet"
              />
              <span>
                <strong>No class yet</strong>
                <span>Send the invite and add class access later.</span>
              </span>
            </label>
          </div>
        </Form.Group>

        {addClassesNow && (
          <div className="invite-teacher-section">
            <Form.Group controlId="teacherInviteClassSearch">
              <Form.Label>Which classes?</Form.Label>
              <Form.Text as="div" className="invite-teacher-field-label">
                Search class
              </Form.Text>
              <Form.Control
                type="search"
                value={classSearchTerm}
                onChange={(event) => {
                  setClassSearchTerm(event.target.value);
                  setError("");
                }}
                placeholder="Start typing a class name"
                aria-label="Search class"
              />
            </Form.Group>

            <div className="invite-teacher-results" aria-label="Matching classes">
              {renderClassResults()}
            </div>
            {submitAttempted && addClassesNow && !hasSelectedClasses && (
              <p className="invite-teacher-validation">Choose at least one class or select No class yet.</p>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <span className="invite-teacher-footer-note">{renderSelectedClassSummary()}</span>
        <Button variant="outline-secondary" type="button" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" type="button" disabled={!canSendInvite} onClick={sendInvite}>
          {isSubmitting
            ? "Sending..."
            : matchingTeacher
              ? "Review existing teacher"
              : "Send invite"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderTeacherFound = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title>Teacher Already Found</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <div className="invite-teacher-found-panel">
          <strong>{existingTeacher?.name || trimmedTeacherName}</strong>
          <span>{existingTeacher?.email || trimmedTeacherEmail}</span>
          <span>This email already belongs to a teacher on the platform.</span>
        </div>

        <Form.Group className="invite-teacher-section" controlId="teacherInviteExistingTeacherAction">
          <Form.Label>What should happen next?</Form.Label>
          <div className="invite-teacher-options">
            <label className={`invite-teacher-option${existingTeacherAction === "shareClasses" ? " selected" : ""}${!hasSelectedClasses || !existingTeacherCanShareClasses ? " disabled" : ""}`}>
              <Form.Check
                type="radio"
                name="teacherInviteExistingTeacherAction"
                checked={existingTeacherAction === "shareClasses"}
                disabled={!hasSelectedClasses || !existingTeacherCanShareClasses}
                onChange={() => setExistingTeacherAction("shareClasses")}
                aria-label="Share selected classes"
              />
              <span>
                <strong>Share selected classes</strong>
                <span>
                  {!existingTeacherCanShareClasses
                    ? "This is an invited teacher, so send their setup link first."
                    : hasSelectedClasses
                      ? renderSelectedClassSummary()
                      : "Go back and choose classes first."}
                </span>
              </span>
            </label>
            {existingTeacherNeedsSetupLink ? (
              <label className={`invite-teacher-option${existingTeacherAction === "setupLink" ? " selected" : ""}${!canSendExistingSetupLink ? " disabled" : ""}`}>
                <Form.Check
                  type="radio"
                  name="teacherInviteExistingTeacherAction"
                  checked={existingTeacherAction === "setupLink"}
                  disabled={!canSendExistingSetupLink}
                  onChange={() => setExistingTeacherAction("setupLink")}
                  aria-label="Send setup link"
                />
                <span>
                  <strong>Send setup link</strong>
                  <span>They have already been invited. Send the setup link again.</span>
                </span>
              </label>
            ) : (
              <label className={`invite-teacher-option${existingTeacherAction === "passwordReset" ? " selected" : ""}`}>
                <Form.Check
                  type="radio"
                  name="teacherInviteExistingTeacherAction"
                  checked={existingTeacherAction === "passwordReset"}
                  onChange={() => setExistingTeacherAction("passwordReset")}
                  aria-label="Send password reset link"
                />
                <span>
                  <strong>Send password reset link</strong>
                  <span>Help this teacher get back into their existing account.</span>
                </span>
              </label>
            )}
          </div>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <span className="invite-teacher-footer-note">
          {existingTeacherAction === "shareClasses"
            ? renderSelectedClassSummary()
            : existingTeacherAction === "setupLink"
              ? "Existing invite selected."
              : "Existing teacher selected."}
        </span>
        <Button variant="outline-secondary" type="button" onClick={() => setStage("form")}>
          Back
        </Button>
        <Button
          variant="primary"
          type="button"
          disabled={!canCompleteExistingTeacher}
          onClick={completeExistingTeacherAction}
        >
          {isSubmitting
            ? "Sending..."
            : existingTeacherAction === "shareClasses"
              ? "Share selected classes"
              : existingTeacherAction === "setupLink"
                ? "Send setup link"
              : "Send password reset link"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSuccess = () => {
    const title =
      successAction === "passwordReset"
        ? "Link Sent"
        : successAction === "setupLink"
          ? "Setup Link Sent"
        : successAction === "shareClasses"
          ? "Class Access Ready"
          : "Invite Sent";
    const teacherLabel = existingTeacher?.name || trimmedTeacherName || "The teacher";
    const mainCopy =
      successAction === "passwordReset"
        ? `${teacherLabel} has been sent a password reset link.`
        : successAction === "setupLink"
          ? `${teacherLabel} has been sent a setup link.`
        : successAction === "shareClasses"
          ? `${teacherLabel} can use the selected classes.`
          : hasSelectedClasses
            ? `${teacherLabel} has been invited and class access is ready.`
            : `${teacherLabel} has been invited.`;
    const nextCopy =
      successAction === "passwordReset"
        ? embedded ? "Return to SENSAI, or invite another teacher if you need to add someone else." : "Return to Teacher Dashboard, or invite another teacher if you need to add someone else."
        : successAction === "setupLink"
          ? "They can use the setup email to finish creating their account."
        : hasSelectedClasses
          ? embedded ? "Return to SENSAI, or invite another teacher if you need to add someone else." : "Return to Teacher Dashboard, or invite another teacher if you need to add someone else."
          : "Class access can be added after the teacher sets up their account.";

    return (
      <>
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="success" className="invite-teacher-success">
            <FaCheckCircle aria-hidden="true" />
            <span>
              <strong>{mainCopy}</strong>
              {hasSelectedClasses && <span>{renderSelectedClassSummary()}</span>}
            </span>
          </Alert>
          <div className="invite-teacher-next">
            <strong>What happens next</strong>
            <span>{nextCopy}</span>
          </div>
          {inviteResults.length > 0 && (
            <div className="invite-teacher-next" aria-label="Invite results">
              <strong>Invite results</strong>
              {inviteResults.map((result, index) => (
                <span key={`${result.email || result.name || "invite"}-${index}`}>
                  {result.email || result.name || `Teacher ${index + 1}`}: {result.status || "invited"}
                </span>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <span className="invite-teacher-footer-note">
            {embedded ? "Done returns to SENSAI." : "Done."}
          </span>
          <Button variant="outline-secondary" type="button" onClick={resetForAnotherTeacher}>
            Invite another teacher
          </Button>
          {!embedded && (
            <Button variant="outline-secondary" type="button" onClick={returnToTeacherDashboard}>
              Return to Teacher Dashboard
            </Button>
          )}
          <Button variant="primary" type="button" onClick={handleDone}>
            Close
          </Button>
        </Modal.Footer>
      </>
    );
  };

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      centered
      backdrop="static"
      className="teacher-invite-teacher-modal"
    >
      {stage === "form" && renderForm()}
      {stage === "teacherFound" && renderTeacherFound()}
      {stage === "success" && renderSuccess()}
    </Modal>
  );
}

export default TeacherInviteTeacherModal;
